import type { InputNumberProps } from 'antd';
import React, { useMemo } from 'react';

import { useCountries } from 'app/hooks/data/useCountries';
import { parseInputNumber, parseNumber } from 'app/utils/typing';

import { CurrencyContext } from '../CurrencyContext/CurrencyContext';
import { CurrencyInput } from '../CurrencyInput/CurrencyInput';

const getSymbolFromCurrency = (currency: string, locale?: string) => {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  }).formatToParts(0);

  return parts.find((part) => part.type === 'currency')?.value || '';
};

export const CurrencyWrapper = ({
  children,
  hotel,
  countryId,
  currency,
  countrySlug,
}: Props) => {
  const { data: countries } = useCountries();

  const country: { currency?: string; locale?: string; slug?: string } =
    useMemo(() => {
      if (!countries) {
        return {};
      }

      if (countryId) {
        return countries.find((c) => c.id === countryId) || {};
      }

      if (countrySlug) {
        return countries.find((c) => c.slug === countrySlug) || {};
      }

      if (hotel) {
        const hotelCountryId = hotel.countryId || hotel.address?.countryId;

        return countries.find((c) => c.id === hotelCountryId) || {};
      }

      return {};
    }, [countries, countryId, countrySlug, hotel]);

  if (!countrySlug) {
    countrySlug = country.slug;
  }

  if (!currency) {
    currency = country.currency;
  }

  const locale = country.locale;

  const currencyFormatter = useMemo(() => {
    if (!currency || !locale) {
      return () => '0';
    }

    return (price: number | undefined) => {
      const parsedPrice = parseNumber(price ?? null) || 0;
      const formatter = new Intl.NumberFormat(locale, {
        currency,
        style: 'currency',
        // @ts-ignore -- legacy directive, needs investigation
        notation: 'standard',
        minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
        maximumFractionDigits: 2,
      });

      return formatter.format(parsedPrice);
    };
  }, [currency, locale]);

  const currencySymbol = useMemo(() => {
    if (!currency) {
      return '';
    }

    return getSymbolFromCurrency(currency, locale);
  }, [currency, locale]);

  const currencyInput = (props: InputNumberProps) => (
    <CurrencyInput
      currency={currencySymbol}
      parser={parseInputNumber}
      {...props}
    />
  );

  return (
    <CurrencyContext.Provider
      value={{
        currencyFormatter,
        currencySymbol,
        currencyInput,
        currency,
        locale,
        countrySlug,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

type Props = {
  children: React.ReactNode;
  hotel?: { countryId?: number; address?: { countryId?: number } } | null;
  countryId?: number;
  currency?: string;
  countrySlug?: string;
};
